import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FirmCompany as CreatedFirmCompany, FirmsService } from 'api';
import { globalAction } from 'common/actions';
import { ERROR_409 } from 'common/config/api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useResponse } from 'services/hooks/useResponse';
import { useRefreshFirmCompanies } from '../useRefreshFirmCompanies';
import { CreateFirmCompanyPayload, UseCreateFirmCompanyReturn } from './useCreateFirmCompany.types';

const DEFAULT_CREATE_COMPANY_ERROR_MESSAGE = 'An error occurred while creating the Company';
const CREATE_COMPANY_ERROR_MESSAGES = {
  [ERROR_409]: 'A company with this name already exists.',
};

const useCreateFirmCompany = (): UseCreateFirmCompanyReturn => {
  const [{ firmId }, dispatch] = useStore() as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();
  const { processErrorResponse } = useResponse();
  const { refreshFirmCompanies } = useRefreshFirmCompanies();

  const { data, mutateAsync } = useMutation({
    mutationFn: (companyData: CreateFirmCompanyPayload) =>
      FirmsService.apiFirmsCompaniesNewCreate(companyData as CreatedFirmCompany),
  });

  const createCompany = useCallback(
    async (companyData: CreateFirmCompanyPayload): Promise<CreatedFirmCompany | null> => {
      dispatch(globalAction.showLoadingProgress(true));

      try {
        const newCompany = await mutateAsync(companyData);

        if (!newCompany?.id) {
          return null;
        }

        enqueueSnackbar(`${newCompany.name} successfully created`, { variant: 'success' });
        await refreshFirmCompanies({ firmId: newCompany.firm ?? firmId });

        return newCompany;
      } catch (error: any) {
        const status = error?.status ?? error?.response?.status;

        processErrorResponse({
          error,
          defaultErrorMessage:
            CREATE_COMPANY_ERROR_MESSAGES[status as keyof typeof CREATE_COMPANY_ERROR_MESSAGES] ??
            DEFAULT_CREATE_COMPANY_ERROR_MESSAGE,
          action: 'create a new company',
        });
        return null;
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    },
    [dispatch, enqueueSnackbar, firmId, mutateAsync, processErrorResponse, refreshFirmCompanies]
  );

  return {
    data: data ?? null,
    createCompany,
  };
};

export default useCreateFirmCompany;
